import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Canvas Settings
  orientation: 'landscape', // 'landscape' | 'portrait'
  designMode: 'image', // 'image' | 'video'
  dimensions: {
    landscape: { width: 1920, height: 1080 },
    portrait: { width: 1080, height: 1920 },
  },
  
  // Canvas Elements
  elements: [], // Array of { id, type, properties, position, size, animations }
  selectedElementId: null,
  
  // Background
  background: {
    type: 'color', // 'color' | 'image' | 'video'
    value: '#FFFFFF', // color hex, image URL, or video URL
    opacity: 100, // opacity percentage (0-100)
  },
  
  // Canvas State
  zoom: 1,
  isPlaying: false, // Preview mode
  
  // Video Timeline State
  videoTime: {
    currentTime: 0, // Current time in seconds
    duration: 30, // Total duration in seconds (default 30s)
    isPlaying: false, // Video playback state
  },
  
  // History (for undo/redo)
  past: [],
  future: [],
  
  // Project metadata
  projectName: 'Untitled Menu',
  lastSaved: null,
  isDirty: false,
  canvasId: null, // ID for tracking canvas saves
};

const canvasSlice = createSlice({
  name: 'canvas',
  initialState,
  reducers: {
    // Orientation & Mode
    setOrientation: (state, action) => {
      state.orientation = action.payload;
      state.isDirty = true;
    },
    setDesignMode: (state, action) => {
      state.designMode = action.payload;
      state.isDirty = true;
    },
    updateCanvasDimensions: (state, action) => {
      const { width, height } = action.payload;
      state.dimensions[state.orientation] = {
        width: width || state.dimensions[state.orientation].width,
        height: height || state.dimensions[state.orientation].height,
      };
      state.isDirty = true;
    },
    
    // Elements Management
    addElement: (state, action) => {
      const newElement = {
        id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: action.payload.type, // 'text' | 'image' | 'video' | 'menuItem' | 'shape'
        position: action.payload.position || { x: 100, y: 100 },
        size: action.payload.size || { width: 200, height: 100 },
        properties: action.payload.properties || {},
        animations: action.payload.animations || [],
        zIndex: state.elements.length,
        locked: false,
        visible: true,
      };
      state.elements.push(newElement);
      state.selectedElementId = newElement.id;
      state.isDirty = true;
    },
    
    updateElement: (state, action) => {
      const { id, updates } = action.payload;
      const element = state.elements.find(el => el.id === id);
      if (element) {
        Object.assign(element, updates);
        state.isDirty = true;
      }
    },
    
    updateElementPosition: (state, action) => {
      const { id, position } = action.payload;
      const element = state.elements.find(el => el.id === id);
      if (element) {
        element.position = position;
        state.isDirty = true;
      }
    },
    
    updateElementSize: (state, action) => {
      const { id, size } = action.payload;
      const element = state.elements.find(el => el.id === id);
      if (element) {
        element.size = size;
        state.isDirty = true;
      }
    },
    
    updateElementProperties: (state, action) => {
      const { id, properties } = action.payload;
      const element = state.elements.find(el => el.id === id);
      if (element) {
        element.properties = { ...element.properties, ...properties };
        state.isDirty = true;
      }
    },
    
    deleteElement: (state, action) => {
      state.elements = state.elements.filter(el => el.id !== action.payload);
      if (state.selectedElementId === action.payload) {
        state.selectedElementId = null;
      }
      state.isDirty = true;
    },
    
    duplicateElement: (state, action) => {
      const element = state.elements.find(el => el.id === action.payload);
      if (element) {
        const duplicate = {
          ...element,
          id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          position: {
            x: element.position.x + 20,
            y: element.position.y + 20,
          },
          zIndex: state.elements.length,
        };
        state.elements.push(duplicate);
        state.selectedElementId = duplicate.id;
        state.isDirty = true;
      }
    },
    
    selectElement: (state, action) => {
      state.selectedElementId = action.payload;
    },
    
    deselectElement: (state) => {
      state.selectedElementId = null;
    },
    
    reorderElement: (state, action) => {
      const { id, direction } = action.payload; // 'up' | 'down' | 'top' | 'bottom'
      const element = state.elements.find(el => el.id === id);
      if (!element) return;
      
      const currentIndex = state.elements.indexOf(element);
      state.elements.splice(currentIndex, 1);
      
      let newIndex;
      switch (direction) {
        case 'top':
          newIndex = state.elements.length;
          break;
        case 'bottom':
          newIndex = 0;
          break;
        case 'up':
          newIndex = Math.min(currentIndex + 1, state.elements.length);
          break;
        case 'down':
          newIndex = Math.max(currentIndex - 1, 0);
          break;
        default:
          newIndex = currentIndex;
      }
      
      state.elements.splice(newIndex, 0, element);
      state.elements.forEach((el, idx) => {
        el.zIndex = idx;
      });
      state.isDirty = true;
    },
    
    // Background Management
    setBackground: (state, action) => {
      state.background = action.payload;
      state.isDirty = true;
    },
    
    // Canvas Controls
    setZoom: (state, action) => {
      state.zoom = Math.max(0.1, Math.min(2, action.payload));
    },
    
    setIsPlaying: (state, action) => {
      state.isPlaying = action.payload;
    },
    
    // Video Timeline Actions
    setVideoCurrentTime: (state, action) => {
      state.videoTime.currentTime = Math.max(0, Math.min(action.payload, state.videoTime.duration));
    },
    setVideoDuration: (state, action) => {
      state.videoTime.duration = Math.max(1, action.payload);
      // Ensure currentTime doesn't exceed new duration
      if (state.videoTime.currentTime > state.videoTime.duration) {
        state.videoTime.currentTime = state.videoTime.duration;
      }
    },
    setVideoPlaying: (state, action) => {
      state.videoTime.isPlaying = action.payload;
    },
    updateAnimationTiming: (state, action) => {
      const { elementId, animationId, ...updates } = action.payload;
      const element = state.elements.find(el => el.id === elementId);
      if (element && element.animations) {
        const animation = element.animations.find(anim => anim.id === animationId);
        if (animation) {
          Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined) return;
            if (key === 'delay' || key === 'duration') {
              animation[key] = Math.max(0, value);
            } else {
              animation[key] = value;
            }
          });
          state.isDirty = true;
        }
      }
    },
    
    // Project Management
    setProjectName: (state, action) => {
      state.projectName = action.payload;
      state.isDirty = true;
    },
    
    loadProject: (state, action) => {
      const project = action.payload;
      return {
        ...state,
        ...project,
        canvasId: project.canvasId || state.canvasId, // Preserve or set canvasId
        lastSaved: new Date().toISOString(),
        isDirty: false,
      };
    },
    
    setCanvasId: (state, action) => {
      state.canvasId = action.payload;
    },
    
    resetCanvas: () => initialState,
    
    saveProject: (state) => {
      state.lastSaved = new Date().toISOString();
      state.isDirty = false;
    },
    
    // Animations
    addAnimationToElement: (state, action) => {
      const { elementId, animation } = action.payload;
      const element = state.elements.find(el => el.id === elementId);
      if (element) {
        if (!element.animations) {
          element.animations = [];
        }
        element.animations.push({
          id: `anim-${Date.now()}`,
          ...animation,
        });
        state.isDirty = true;
      }
    },
    
    removeAnimationFromElement: (state, action) => {
      const { elementId, animationId } = action.payload;
      const element = state.elements.find(el => el.id === elementId);
      if (element && element.animations) {
        element.animations = element.animations.filter(anim => anim.id !== animationId);
        state.isDirty = true;
      }
    },
    
    // Undo/Redo
    undo: (state) => {
      if (state.past.length === 0) return;
      
      const previous = state.past[state.past.length - 1];
      const newPast = state.past.slice(0, state.past.length - 1);
      
      state.future.push({
        elements: state.elements,
        background: state.background,
        orientation: state.orientation,
      });
      
      state.past = newPast;
      state.elements = previous.elements;
      state.background = previous.background;
      state.orientation = previous.orientation;
    },
    
    redo: (state) => {
      if (state.future.length === 0) return;
      
      const next = state.future[state.future.length - 1];
      const newFuture = state.future.slice(0, state.future.length - 1);
      
      state.past.push({
        elements: state.elements,
        background: state.background,
        orientation: state.orientation,
      });
      
      state.future = newFuture;
      state.elements = next.elements;
      state.background = next.background;
      state.orientation = next.orientation;
    },
    
    saveToHistory: (state) => {
      state.past.push({
        elements: JSON.parse(JSON.stringify(state.elements)),
        background: { ...state.background },
        orientation: state.orientation,
      });
      state.future = [];
      
      // Limit history to 50 states
      if (state.past.length > 50) {
        state.past = state.past.slice(1);
      }
    },
  },
});

export const {
  setOrientation,
  setDesignMode,
  updateCanvasDimensions,
  addElement,
  updateElement,
  updateElementPosition,
  updateElementSize,
  updateElementProperties,
  deleteElement,
  duplicateElement,
  selectElement,
  deselectElement,
  reorderElement,
  setBackground,
  setZoom,
  setIsPlaying,
  setProjectName,
  loadProject,
  resetCanvas,
  saveProject,
  setCanvasId,
  addAnimationToElement,
  removeAnimationFromElement,
  setVideoCurrentTime,
  setVideoDuration,
  setVideoPlaying,
  updateAnimationTiming,
  undo,
  redo,
  saveToHistory,
} = canvasSlice.actions;

export default canvasSlice.reducer;

